import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingWrapper } from "../../LoadingWrapper/LoadingWrapper";
import { StaffHeader } from "../Staff/StaffHeader/StaffHeader";
import { AttributesTab } from "./AttributesTab";
import { BadgesTab } from "./BadgesTab";
import { PersonalTab } from "./PersonalTab/PersonalTab";
import { ProfileApprovalBanner } from "./ProfileApprovalBanner";
import { StaffDetail } from "./queries.graphql";
import { StaffDetailQuery } from "./queries.types";
import { ShiftAttendanceTable } from "./ShiftTable/ShiftAttendanceTable";

interface StaffDetailPanelProps {
  staffId: string;
}

export enum StaffDetailPanelTabs {
  Personal,
  AllAttributes,
  Badges,
}

export const StaffDetailPanel: React.FC<StaffDetailPanelProps> = ({ staffId }) => {
  const { data, refetch } = useQueryHandler<StaffDetailQuery>(StaffDetail, { id: staffId });
  const [tab, setTab] = useState<StaffDetailPanelTabs>(StaffDetailPanelTabs.Personal);
  const { t } = useTranslation();

  const handleReviewComplete = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (!data?.worker) {
    return <LoadingWrapper on sx={{ height: "100%" }} />;
  }

  const handleChangeTab = (_: React.SyntheticEvent, value: StaffDetailPanelTabs) => {
    setTab(value);
  };

  return (
    <Box
      sx={{
        position: "relative",
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{
          padding: 2,
          position: "relative",
        }}
      >
        {data.worker.workerinfo !== null && (
          <Grid size={12}>
            <ProfileApprovalBanner
              declineMessage={data.worker.workerinfo.declineMessage}
              onReviewComplete={handleReviewComplete}
              profileApprovalStatus={data.worker.workerinfo.profileApprovalStatus}
              workerId={staffId}
            />
          </Grid>
        )}
        <Grid size={12}>
          <StaffHeader staff={data.worker} />
        </Grid>
        <Grid size={12}>
          <Tabs
            onChange={handleChangeTab}
            sx={(theme) => ({
              minHeight: "unset",
              "& .MuiButtonBase-root.MuiTab-root": {
                minHeight: "unset",
                padding: theme.spacing(0.5, 1),
              },
              borderBottom: 1,
              borderColor: theme.palette.grey[400],
            })}
            value={tab}
          >
            <Tab label={t("ShiftTimeline.personal")} value={StaffDetailPanelTabs.Personal} />
            <Tab label={t("ShiftTimeline.attributes")} value={StaffDetailPanelTabs.AllAttributes} />
            <Tab label={t("ShiftTimeline.badges")} value={StaffDetailPanelTabs.Badges} />
          </Tabs>
        </Grid>
        <Grid
          size={12}
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {tab === StaffDetailPanelTabs.Personal && <PersonalTab staff={data.worker} />}
          {tab === StaffDetailPanelTabs.AllAttributes && <AttributesTab staff={data.worker} />}
          {tab === StaffDetailPanelTabs.Badges && <BadgesTab staff={data.worker} />}
        </Grid>
      </Grid>
      {tab === StaffDetailPanelTabs.Personal && (
        <ShiftAttendanceTable
          attendances={data.worker.attendances}
          count={data.worker.attendanceStatistics.totalCount}
          workerId={data.worker.id}
        />
      )}
    </Box>
  );
};
